export type Side = 'top' | 'bottom' | 'left' | 'right';
export type Placement = Side | `${Side}-start` | `${Side}-end`;

export interface PositionOptions {
  placement?: Placement;
  /** Gap between the anchor and the floating element, in px. */
  offset?: number;
  /** Minimum distance kept from the viewport edges, in px. */
  padding?: number;
}

export interface PositionResult {
  x: number;
  y: number;
  placement: Placement;
  /** Anchor centre relative to the floating element (for arrows). */
  arrowX: number;
  arrowY: number;
}

const opposite: Record<Side, Side> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };

/**
 * Computes viewport coordinates (for `position: fixed`) that place `floating`
 * next to `anchor`, flipping to the other side and shifting along the edge
 * when there is not enough room.
 */
export function computePosition(anchor: Element, floating: HTMLElement, options: PositionOptions = {}): PositionResult {
  const { placement = 'bottom', offset = 8, padding = 8 } = options;
  const a = anchor.getBoundingClientRect();
  const fw = floating.offsetWidth;
  const fh = floating.offsetHeight;
  const vw = document.documentElement.clientWidth;
  const vh = document.documentElement.clientHeight;

  let [side, align] = placement.split('-') as [Side, 'start' | 'end' | undefined];

  const room: Record<Side, number> = {
    top: a.top - padding,
    bottom: vh - a.bottom - padding,
    left: a.left - padding,
    right: vw - a.right - padding,
  };
  const needed = side === 'top' || side === 'bottom' ? fh + offset : fw + offset;
  if (room[side] < needed && room[opposite[side]] > room[side]) side = opposite[side];

  let x: number;
  let y: number;
  if (side === 'top' || side === 'bottom') {
    y = side === 'top' ? a.top - fh - offset : a.bottom + offset;
    x = align === 'start' ? a.left : align === 'end' ? a.right - fw : a.left + a.width / 2 - fw / 2;
  } else {
    x = side === 'left' ? a.left - fw - offset : a.right + offset;
    y = align === 'start' ? a.top : align === 'end' ? a.bottom - fh : a.top + a.height / 2 - fh / 2;
  }

  x = Math.min(Math.max(x, padding), Math.max(padding, vw - fw - padding));
  y = Math.min(Math.max(y, padding), Math.max(padding, vh - fh - padding));

  return {
    x: Math.round(x),
    y: Math.round(y),
    placement: (align ? `${side}-${align}` : side) as Placement,
    arrowX: Math.round(a.left + a.width / 2 - x),
    arrowY: Math.round(a.top + a.height / 2 - y),
  };
}

/** Re-runs `update` on scroll (any ancestor) and resize until the returned cleanup is called. */
export function autoUpdate(anchor: Element, floating: HTMLElement, update: () => void): () => void {
  let frame = 0;
  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      update();
    });
  };
  window.addEventListener('scroll', schedule, { capture: true, passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
  ro?.observe(anchor);
  ro?.observe(floating);
  return () => {
    cancelAnimationFrame(frame);
    window.removeEventListener('scroll', schedule, { capture: true });
    window.removeEventListener('resize', schedule);
    ro?.disconnect();
  };
}

/** Applies a computed position to an element shown with `position: fixed`. */
export function applyPosition(floating: HTMLElement, pos: PositionResult): void {
  floating.style.left = `${pos.x}px`;
  floating.style.top = `${pos.y}px`;
  floating.dataset.placement = pos.placement;
}

export const supportsPopover = typeof HTMLElement !== 'undefined' && 'popover' in HTMLElement.prototype;

export function showPopover(el: HTMLElement): void {
  if (supportsPopover && !el.matches(':popover-open')) el.showPopover();
  el.toggleAttribute('data-open', true);
}

export function hidePopover(el: HTMLElement): void {
  if (supportsPopover && el.matches(':popover-open')) el.hidePopover();
  el.toggleAttribute('data-open', false);
}
